import React from "react";

import { AgGridReact } from "ag-grid-react";
import { GridPagination, GridPaginationRef } from "./Grid/GridPagination";
import { AllCommunityModule, ColDef, ModuleRegistry, RowClickedEvent, RowDoubleClickedEvent, RowSelectionOptions, SelectionChangedEvent } from "ag-grid-community";

import { makeStyles } from "@fluentui/react-components";
import { useAppContext } from "../contexts/AppContext";

import GridLoadingOverlay from "./Grid/GridLoadingOverlay";

ModuleRegistry.registerModules([AllCommunityModule]);

const useStyles = makeStyles({
    gridWrapper: {
        height: "calc(100% - 140px)",
        width: "100%",
    },
});

type BaseGridProps<Type> = {
    isLoading: boolean;
    data: Type[] | undefined;
    colDefs: ColDef[];
    isSingle: boolean;
    onRowClick?: (e: RowClickedEvent) => void;
    onRowDoubleClick?: (e: RowDoubleClickedEvent) => void;
    onSelectionChanged?: (e: SelectionChangedEvent) => void;
    onReset?: () => void;
};

export default function BaseGrid<Type>({ isLoading, data, colDefs, isSingle, onRowClick, onRowDoubleClick, onSelectionChanged, onReset }: BaseGridProps<Type>) {
    const styles = useStyles();
    const { gridTheme } = useAppContext();

    const gridRef = React.useRef<AgGridReact<Type>>(null);
    const paginationRef = React.useRef<GridPaginationRef>(null);

    const rowSelection = React.useMemo<RowSelectionOptions>(() => {
        return {
            mode: isSingle ? "singleRow" : "multiRow",
            checkboxes: true,
            enableClickSelection: true,
        };
    }, [isSingle]);

    const loadingParams = React.useMemo(() => {
        return { loadingMessage: "Loading data..." };
    }, []);

    const defaultColDef = React.useMemo<ColDef>(() => {
        return {
            editable: false,
            sortable: true,
            filter: true,
            filterParams: {
                maxNumConditions: 1,
            },
            lockPosition: "left",
            flex: 1,
        };
    }, []);

    const onPaginationChanged = React.useCallback(() => {
        paginationRef.current?.invoke();
    }, []);

    React.useEffect(() => {
        if (onReset) {
            onReset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={styles.gridWrapper}>
            <AgGridReact<Type>
                ref={gridRef}
                loading={isLoading}
                theme={gridTheme}
                rowSelection={rowSelection}
                loadingOverlayComponentParams={loadingParams}
                rowData={data}
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                pagination={true}
                paginationPageSize={100}
                suppressPaginationPanel={true}
                loadingOverlayComponent={GridLoadingOverlay}
                onPaginationChanged={onPaginationChanged}
                onRowClicked={onRowClick}
                onRowDoubleClicked={onRowDoubleClick}
                onSelectionChanged={onSelectionChanged}
                selectionColumnDef={{pinned: 'left'}}
            />
            <GridPagination gridRef={gridRef} ref={paginationRef} />
        </div>
    );
}